import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "@/store/useApp";
import { hasPermission } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/api";
import {
  downloadRankingCsv,
  fetchProcess,
  fetchRanking,
  type ProcessDetail,
  type RankingRow,
  type SelectionDecision,
} from "@/lib/adminSelectionApi";

const DECISION_LABEL: Record<SelectionDecision, string> = {
  NAO_APROVAR: "Não aprovar",
  MANTER: "Manter",
  APROVAR: "Aprovar",
  PRIORIDADE: "Prioridade",
};

export default function AdminSelectionRanking() {
  const { processId = "" } = useParams();
  const navigate = useNavigate();
  const canExport = hasPermission(useApp((s) => s.account), "SELECAO.RANKING.EXPORTAR");
  const [process, setProcess] = useState<ProcessDetail | null>(null);
  const [rows, setRows] = useState<RankingRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([fetchProcess(processId), fetchRanking(processId)]);
      setProcess(p);
      setRows(r);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Falha ao carregar ranking"));
    } finally {
      setLoading(false);
    }
  }, [processId]);

  useEffect(() => { void load(); }, [load]);

  const columns = rows[0]?.criteria ?? [];
  const maxScore = 5;

  return (
    <div className="container mx-auto max-w-6xl px-4 py-6">
      <button className="mb-3 text-sm text-muted-foreground underline" onClick={() => navigate("/admin/selecao")}>← Processos</button>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Ranking</h1>
          <p className="text-sm text-muted-foreground">{process?.name}</p>
        </div>
        {canExport && <Button size="sm" variant="ghost" onClick={() => void downloadRankingCsv(processId)}>Exportar CSV</Button>}
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-muted-foreground">Carregando…</p>
      ) : rows.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">Nenhum candidato para ranquear.</p>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-left">
              <tr>
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Candidato</th>
                <th className="px-3 py-2">Nota</th>
                {columns.map((c) => <th key={c.criterionKey} className="px-3 py-2 text-xs">{c.label}</th>)}
                <th className="px-3 py-2">Potencial</th>
                <th className="px-3 py-2">Decisão</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.enrollmentId} className="cursor-pointer border-t border-border hover:bg-secondary/20"
                  onClick={() => navigate(`/admin/selecao/candidatos/${r.enrollmentId}`)}>
                  <td className="px-3 py-3 font-mono">{r.position}</td>
                  <td className="px-3 py-3 font-medium">{r.candidateName || "—"}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono">{r.finalScore == null ? "—" : r.finalScore.toFixed(2).replace(".", ",")}</span>
                      {r.finalScore != null && (
                        <span className="hidden h-1.5 w-20 overflow-hidden rounded bg-secondary sm:inline-block">
                          <span className="block h-full bg-primary" style={{ width: `${Math.min(100, (r.finalScore / maxScore) * 100)}%` }} />
                        </span>
                      )}
                      {r.partialScore && <span className="text-[11px] text-muted-foreground">parcial</span>}
                    </div>
                  </td>
                  {r.criteria.map((c) => (
                    <td key={c.criterionKey} className="px-3 py-3 font-mono text-xs">{c.score ?? "—"}</td>
                  ))}
                  <td className="px-3 py-3 font-mono">{r.potential ?? "—"}</td>
                  <td className="px-3 py-3">{r.decision ? DECISION_LABEL[r.decision] : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        Ordenado por nota final e pelos critérios de desempate do processo. A decisão final é sempre manual.
      </p>
    </div>
  );
}
